import type { fetchAdminFaxes } from '@/lib/apiClient';

type LinkableWorkflowType = 'REFERRAL' | 'MEDICAL_RECORD_REQUEST';

/**
 * TanStack Query key for a linked record's admin documents list, keyed by
 * workflow type. Shared by the inbox and the record detail pages so linking,
 * re-linking, and unlinking invalidate the same cache entry a record's
 * Documents card reads from.
 */
export function documentsQueryKey(
  recordId: string,
  workflowType: LinkableWorkflowType | null
): [string, string] {
  return workflowType === 'REFERRAL'
    ? ['referralDocuments', recordId]
    : ['adminRecordsRequestDocuments', recordId];
}

type AdminFaxListArgs = Parameters<typeof fetchAdminFaxes>;
type FaxDirection = AdminFaxListArgs[0];
type AdminFaxListParams = NonNullable<AdminFaxListArgs[1]>;

const adminFaxesByDirection = (direction: FaxDirection) =>
  ['adminFaxes', direction] as const;

/**
 * TanStack Query keys for the admin fax lists and the sidebar's unlinked-fax
 * count. Every key sits under `direction(…)`, so one invalidation after a fax
 * is linked, unlinked, deleted or restored refreshes the lists and the badge.
 * Pass `list` the same params object the query sends to `fetchAdminFaxes`.
 */
export const adminFaxKeys = {
  direction: adminFaxesByDirection,
  list: (direction: FaxDirection, params: AdminFaxListParams) =>
    [...adminFaxesByDirection(direction), 'list', params] as const,
  unlinkedCount: () =>
    [...adminFaxesByDirection('INBOUND'), 'unlinkedCount'] as const,
};
